import { MirinaeSanitizer } from "../../../utils/MirinaeSanitizer.js";
import { MirinaeSelection } from "../../../utils/MirinaeSelection.js";
import { MirinaeToolbar } from "../../../components/common/MirinaeToolbar.js"; // Class Import
import "../../../styles/mirinae-editor.css";

/**
 * Webnovel Editor Area
 * Handles the content editing of a single chapter.
 */
export class WebnovelEditorArea {
    constructor(container, props) {
        this.container = container;
        this.props = props; // { chapter, onUpdate }

        // [FIX] Explicitly Instantiate Toolbar
        // This ensures the code runs and attaches to DOM, bypassing tree-shaking issues with singletons.
        // We use the default selector '.rich-text' which matches our editor div.
        this.toolbar = new MirinaeToolbar('.rich-text');
    }

    render() {
        if (this.toolbar) this.toolbar.init();

        const data = this.props.chapter;
        if (!data) {
            this.container.innerHTML = '<div class="text-zinc-500 text-center mt-20">챕터를 선택해주세요.</div>';
            return;
        }

        // [Rich Text] Prepare Initial Content
        const initialHtml = MirinaeSanitizer.isHtml(data.content)
            ? MirinaeSanitizer.sanitize(data.content)
            : MirinaeSanitizer.convertLegacyToHtml(data.content);

        this.container.innerHTML = `
            <div class="max-w-2xl mx-auto space-y-6 animate-fade-in pb-20">
                <!-- Header: Title -->
                <div class="space-y-3 p-4 bg-zinc-800/20 border border-white/5 rounded-xl">
                    <div class="flex items-center justify-between">
                        <label class="text-xs font-bold text-indigo-400 uppercase">${window.locale.t('editor.episode_title')}</label>
                    </div>
                    <input type="text" id="active-title" class="w-full bg-zinc-800/50 border border-white/10 rounded-lg p-3 text-lg font-bold text-white focus:border-indigo-500 outline-none placeholder-zinc-700 transition-colors" value="${data.title || ''}" placeholder="${window.locale.t('editor.episode_placeholder')}">
                </div>

                <!-- BGM -->
                <div class="space-y-1 px-1">
                     <label class="block text-xs font-bold text-zinc-500 uppercase flex items-center gap-1">
                        <span class="material-symbols-outlined text-sm">music_note</span> ${window.locale.t('editor.bgm_label')}
                     </label>
                     <div class="flex items-center gap-2">
                        <input type="text" id="active-bgm" class="flex-grow bg-zinc-800/30 border border-white/10 rounded-lg p-2.5 text-xs text-zinc-300 focus:border-indigo-500 outline-none placeholder-zinc-700 font-mono transition-colors" placeholder="https://cdn1.suno.ai/..." value="${data.bgmUrl || ''}">
                    </div>
                </div>

                <!-- Content Area -->
                <div class="space-y-2 h-full flex flex-col">
                    <div class="flex justify-between items-center px-1">
                        <label class="text-xs font-bold text-zinc-500 uppercase">${window.locale.t('editor.content_body')}</label>
                        <label class="cursor-pointer text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 bg-white/5 px-2 py-1 rounded hover:bg-white/10 transition-colors border border-white/5">
                            <span class="material-symbols-outlined text-xs">upload_file</span> ${window.locale.t('editor.load_txt')}
                            <input type="file" id="active-txt-upload" class="hidden" accept=".txt">
                        </label>
                    </div>
                    <!-- Rich Text Editor -->
                    <div id="active-content" 
                         class="rich-text w-full min-h-[500px] bg-zinc-800/30 border border-white/10 rounded-xl p-6 text-base leading-loose text-zinc-300 focus:border-indigo-500/50 focus:bg-zinc-800/50 outline-none resize-none font-serif custom-scrollbar transition-colors" 
                         contenteditable="true" 
                         placeholder=${window.locale.t('editor.content_placeholder')} 
                         spellcheck="false">${initialHtml}</div>
                </div>

                <!-- Author Note -->
                <div class="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-4 mt-8">
                    <div class="text-xs text-indigo-400 mb-2 font-bold flex items-center gap-1">
                        <span class="material-symbols-outlined text-sm">edit_note</span> ${window.locale.t('editor.author_note')}
                    </div>
                    <textarea id="active-note" class="w-full h-20 bg-transparent border-none p-0 text-sm text-zinc-400 focus:ring-0 outline-none resize-none placeholder-indigo-500/30" placeholder="${window.locale.t('editor.note_placeholder')}">${data.note || ''}</textarea>
                </div>
            </div>
        `;

        this.bindEvents();
    }

    bindEvents() {
        const data = this.props.chapter;
        const container = this.container;

        const titleEl = container.querySelector('#active-title');
        const contentEl = container.querySelector('#active-content'); // Now a DIV
        const noteEl = container.querySelector('#active-note');
        const bgmEl = container.querySelector('#active-bgm');

        // Inputs
        titleEl.oninput = () => {
            data.title = titleEl.value;
            this.props.onUpdate('title');
        };

        // [Rich Text] Paste Handling
        contentEl.addEventListener('paste', (e) => {
            e.preventDefault();
            // Get plain text first (Security)
            const text = (e.originalEvent || e).clipboardData.getData('text/plain');
            document.execCommand("insertText", false, text);
        });

        // [Rich Text] Input Sync
        contentEl.oninput = () => {
            // Sync innerHTML to State
            data.content = contentEl.innerHTML;
            this.props.onUpdate('content');

            // Re-trigger Toolbar position if needed (Handled by selectionchange globally)
        };

        noteEl.oninput = () => {
            data.note = noteEl.value;
            this.props.onUpdate('note');
        };

        bgmEl.onchange = () => {
            data.bgmUrl = bgmEl.value;
            this.props.onUpdate('bgm');
        };

        // Text Upload
        container.querySelector('#active-txt-upload').onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
                // Determine if file is text or html... usually txt is plain.
                // Convert \n to <br> for rich text
                const text = ev.target.result;
                const html = MirinaeSanitizer.convertLegacyToHtml(text);

                contentEl.innerHTML = html;
                data.content = html; // Sync state immediately
                contentEl.dispatchEvent(new Event('input'));
                this.props.onUpdate('content');
            };
            reader.readAsText(file);
        };
    }
}
